import isEmpty from 'lodash/isEmpty';

import { AdvancedLinkingEvaluator } from './advancedLinkingEvaluator';
import { isSamePage } from './pageNavigation';
import { scrollNavigation } from './scrollToComponent';

import type { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
import type { AdvancedLinking } from '$lib/types/AdvancedLinking';

interface LinkingNavigationParams {
    componentId: string;
    userAnswers: Record<string, string>;
    advancedLinking: AdvancedLinking;
    nextPageNavigator: NextPageNavigator;
    originalLogicNextPage: string; // from original Linking logic
    nextPage: string;
    externalLink?: string;
    isExternal?: boolean;
    isNewTab?: boolean;
    currentPageId: string;
    linkingComponentId?: string;
}

const handleScrollAction = ({
    pageId,
    componentId,
    currentPageId,
    goToPageAndScroll,
}: {
    pageId?: string;
    componentId: string;
    currentPageId: string;
    goToPageAndScroll: (pageId: string, componentId: string) => Promise<void>;
}) => {
    if (isSamePage(pageId, currentPageId)) {
        scrollNavigation.scrollOnSamePage(componentId);
        return;
    }

    if (pageId) {
        return goToPageAndScroll(pageId, componentId);
    }
};

export const handleLinkingNavigation = ({
    componentId,
    userAnswers,
    advancedLinking,
    nextPageNavigator,
    originalLogicNextPage,
    nextPage,
    externalLink = '',
    isExternal = false,
    isNewTab = false,
    currentPageId,
    linkingComponentId,
}: LinkingNavigationParams): Promise<void> | void => {
    // Check for advanced linking rules
    if (componentId && !isEmpty(advancedLinking)) {
        const advancedLinkingResult = AdvancedLinkingEvaluator.evaluate(
            componentId,
            userAnswers,
            advancedLinking,
        );

        if (advancedLinkingResult) {
            // Handle scroll action from advanced linking
            if (advancedLinkingResult.target === 'scroll' && advancedLinkingResult.componentId) {
                return handleScrollAction({
                    pageId: advancedLinkingResult.pageId,
                    componentId: advancedLinkingResult.componentId,
                    currentPageId,
                    goToPageAndScroll: (pageId, componentId) =>
                        nextPageNavigator.goToPageAndScroll(pageId, componentId),
                });
            }

            // Navigate based on advanced linking result (URL or page)
            if (advancedLinkingResult.isUrl) {
                return nextPageNavigator.goExternal(
                    advancedLinkingResult.target,
                    advancedLinkingResult.isNewTab,
                );
            } else {
                return nextPageNavigator.go(
                    advancedLinkingResult.target === 'next' && nextPage === 'result'
                        ? 'result'
                        : advancedLinkingResult.target === 'next'
                        ? nextPage
                        : advancedLinkingResult.target,
                );
            }
        }
    }

    // Navigate to external page (original logic)
    if (isExternal && externalLink) {
        return nextPageNavigator.goExternal(externalLink, isNewTab);
    }

    // Handle simple linking with scroll (componentId from Linking object)
    if (linkingComponentId) {
        return handleScrollAction({
            pageId: originalLogicNextPage,
            componentId: linkingComponentId,
            currentPageId,
            goToPageAndScroll: (pageId, componentId) =>
                nextPageNavigator.goToPageAndScroll(pageId, componentId),
        });
    }

    return nextPageNavigator.go(originalLogicNextPage);
};

interface LinkingPreloadParams {
    componentId: string;
    userAnswers: Record<string, string>;
    advancedLinking: AdvancedLinking;
    nextPageNavigator: NextPageNavigator;
    originalLogicNextPage: string;
    nextPage: string;
    isExternal: boolean;
}

export const getLinkingPreloadTarget = ({
    componentId,
    userAnswers,
    advancedLinking,
    originalLogicNextPage,
    nextPage,
    isExternal,
}: LinkingPreloadParams): { pageId: string; isExternal: boolean } => {
    // Check for advanced linking rules
    if (componentId && !isEmpty(advancedLinking)) {
        const advancedLinkingResult = AdvancedLinkingEvaluator.evaluate(
            componentId,
            userAnswers,
            advancedLinking,
        );

        if (advancedLinkingResult) {
            // Return preload target based on advanced linking result
            if (advancedLinkingResult.isUrl) {
                return { pageId: '', isExternal: true };
            } else {
                const targetPage =
                    advancedLinkingResult.target === 'next' && nextPage === 'result'
                        ? 'result'
                        : advancedLinkingResult.target === 'next'
                        ? nextPage
                        : advancedLinkingResult.target;

                return { pageId: targetPage, isExternal: false };
            }
        }
    }

    // Return original logic target
    return {
        pageId: isExternal ? '' : originalLogicNextPage,
        isExternal,
    };
};
